<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="3" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Seat Configurations</h3>
              <v-btn
                class="mx-2 mb-2"
                fab
                x-small
                color="primary"
                to="/seat-configurations/create"
              >
                <v-icon dark> mdi-plus </v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="4">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search by name..."
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  >
                  </v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.total_seats"
                    :items="seatOptions"
                    clearable
                    @click:clear="(filter.total_seats = null), (page = 1)"
                    label="Total Seats"
                    @input="getData"
                    prepend-inner-icon="mdi-seat"
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    clearable
                    @click:clear="(filter.is_active = null), (page = 1)"
                    label="Status"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-card elevation="3" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="285"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left sr_no">Sr No</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">Configuration Name</th>
                      <th class="text-left">Total Seats</th>
                      <th class="text-left">VIP Seats</th>
                      <th class="text-left">VIP Positions</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="seatConfigurations.length != 0">
                    <tr
                      v-for="(config, i) in seatConfigurations"
                      :key="`seatconfig_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': seatConfigurations.length - 1 == i,
                      }"
                    >
                      <td style="min-width: 85px">
                        {{ i + 1 }}
                      </td>
                      <td>
                        <v-tooltip bottom v-if="config.is_active == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Active
                            </v-chip>
                          </template>
                          <span>Configuration is Active</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="config.is_active == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Inactive
                            </v-chip>
                          </template>
                          <span>Configuration is Inactive</span>
                        </v-tooltip>
                      </td>
                      <td>
                        <strong>{{ config.name || "N/A" }}</strong>
                      </td>
                      <td>
                        <v-chip small color="primary" text-color="white">
                          <v-icon left small>mdi-seat</v-icon>
                          {{ config.total_seats }}
                        </v-chip>
                      </td>
                      <td>
                        <v-chip
                          small
                          :color="config.vip_seats > 0 ? 'amber' : 'grey'"
                          text-color="white"
                        >
                          <v-icon left small>mdi-crown</v-icon>
                          {{ config.vip_seats }}
                        </v-chip>
                      </td>
                      <td>
                        <span
                          v-if="
                            config.vip_seat_positions &&
                            config.vip_seat_positions.length > 0
                          "
                        >
                          <v-chip
                            v-for="(pos, idx) in config.vip_seat_positions"
                            :key="`pos_${idx}`"
                            x-small
                            class="ma-1"
                            color="amber darken-2"
                            text-color="white"
                          >
                            {{ pos }}
                          </v-chip>
                        </span>
                        <span v-else class="grey--text">-</span>
                      </td>
                      <td class="text-right">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              x-small
                              v-bind="attrs"
                              v-on="on"
                              color="primary"
                              :to="`/seat-configurations/${encryptIt(config.id)}`"
                            >
                              <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit Configuration</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              x-small
                              v-bind="attrs"
                              v-on="on"
                              color="error"
                              @click="
                                selectedConfig = config;
                                deleteDialog = true;
                              "
                            >
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete Configuration</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="7" class="text-center">
                        <p class="mt-5">No Seat Configurations Found</p>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination
                v-if="totalPages > 1"
                class="mt-3"
                v-model="page"
                :length="totalPages"
                @input="getData"
              ></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Delete Configuration?</v-card-title>
        <v-card-text>
          Are you sure you want to delete
          <strong>{{ selectedConfig ? selectedConfig.name : "" }}</strong
          >?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" text @click="deleteConfiguration">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "SeatConfigurationIndex",
  data() {
    return {
      seatConfigurations: [],
      isLoading: false,
      page: 1,
      totalPages: 1,
      // rowsPerPage is provided by global mixin from Vuex store
      filter: {
        search_keyword: null,
        total_seats: null,
        is_active: null,
      },
      seatOptions: [
        { text: "8 Seats", value: 8 },
        { text: "10 Seats", value: 10 },
        { text: "15 Seats", value: 15 },
        { text: "20 Seats", value: 20 },
      ],
      statuses: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      deleteDialog: false,
      selectedConfig: null,
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.rowsPerPage,
          ...this.filter,
        };

        const response = await axios.get("/seats", { params });

        if (response.data.success) {
          this.seatConfigurations = response.data.data;
          this.totalPages = Math.ceil(response.data.count / this.rowsPerPage);
        }
      } catch (error) {
        console.error("Error fetching seat configurations:", error);
        alert(
          error.response?.data?.message || "Failed to fetch configurations"
        );
      } finally {
        this.isLoading = false;
      }
    },

    async deleteConfiguration() {
      if (!this.selectedConfig) return;

      try {
        const response = await axios.delete(
          `/seats/${this.encryptIt(this.selectedConfig.id)}`
        );

        if (response.data.success) {
          alert("Configuration deleted successfully");
          this.deleteDialog = false;
          this.selectedConfig = null;
          this.getData();
        }
      } catch (error) {
        console.error("Error deleting configuration:", error);
        alert(
          error.response?.data?.message || "Failed to delete configuration"
        );
      }
    },
  },
};
</script>

<style scoped>
.striped-table tbody tr:nth-child(even) {
  background-color: #f5f5f5;
}
.sr_no {
  min-width: 85px !important;
}
.action_th {
  min-width: 120px !important;
}
</style>
